/**
 * Feast Day Tone Mappings for Malankara Orthodox Church
 * Maps major feast days to their fixed liturgical tones
 */

/**
 * Fixed feast days (same date every year)
 * Format: "MM-DD": { name: "Feast Name", tone: X, isResetPoint: boolean }
 * Reset points start a new 8-week tone cycle
 */
export const FIXED_FEAST_DAYS = {
  "12-25": {
    name: "Christmas (Yaldo)",
    tone: 1,
    keywords: ["christmas", "yaldo", "nativity"],
    isResetPoint: false, // Cycle resets to Tone 1
  },
  "01-06": {
    name: "Theophany/Epiphany (Denha)",
    tone: 2,
    keywords: ["theophany", "epiphany", "denha", "baptism of the lord"],
    isResetPoint: false,
  },
  "09-14": {
    name: "Feast of the Holy Cross (Sleeba)",
    tone: 8,
    keywords: ["sleeba perunnal"],
    isResetPoint: true,
  },
  "08-15": {
    name: "Ascension of St Mary",
    tone: 7,
    keywords: ["ascension of st mary"],
    isResetPoint: false,
  },
  "01-01": {
    name: "Circumcision of the Lord",
    tone: 8,
    keywords: ["circumcision"],
    isResetPoint: false,
  },
  "03-25": {
    name: "Annunciation to St Mary",
    tone: 4,
    keywords: ["Annunciation to St Mary"],
    isResetPoint: false,
  },
  // Add more fixed feast days as they are discovered
};

/**
 * Movable feast days (dates change each year based on Easter)
 * These will be matched by event name since we fetch full year data
 */
export const MOVABLE_FEAST_PATTERNS = {
  easter: {
    name: "Easter (Qyomtho/Resurrection)",
    tone: 1, // Primary tone for Easter
    keywords: ["kymtho", "resurrection", "qyomtho", "kyomtho"],
    priority: 1, // Highest priority
    isResetPoint: true, // Cycle resets (to Tone 8 for Easter, then continues)
  },
  newSunday: {
    name: "New Sunday",
    tone: 2,
    keywords: ["sunday after easter"],
    priority: 2,
    isResetPoint: false,
  },
  ascension: {
    name: "Ascension",
    tone: 5,
    keywords: ["ascension of our lord"],
    priority: 3,
    isResetPoint: false,
  },
  pentecost: {
    name: "Pentecost",
    tone: 7,
    keywords: ["fiftieth day after easter"],
    priority: 4,
    isResetPoint: false,
  },
  palmSunday: {
    name: "Palm Sunday (Hosanna)",
    tone: 1, // Needs research
    keywords: ["palm sunday"],
    priority: 5,
    isResetPoint: false,
  },
  goodFriday: {
    name: "Good Friday",
    tone: 4, // Needs research
    keywords: ["good friday", "crucifixion"],
    priority: 6,
    isResetPoint: false,
  },
  // Nineveh Lent related
  ninevehStart: {
    name: "Nineveh Lent",
    tone: 6, // According to research, begins with Mode 6 and 2
    keywords: ["nineveh", "ba'uta of ninevites"],
    priority: 7,
    isResetPoint: true, // Cycle resets (to Tone 6)
  },
};

/**
 * Check if a date is a fixed feast day
 * @param {Date} date - The date to check
 * @returns {Object|null} - Feast day info or null
 */
export function getFixedFeastDay(date) {
  const monthDay = `${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
    date.getUTCDate()
  ).padStart(2, "0")}`;
  return FIXED_FEAST_DAYS[monthDay] || null;
}

/**
 * Check if an event name matches a movable feast day pattern
 * @param {string} eventName - The event summary/name from calendar
 * @returns {Object|null} - Feast day info or null
 */
export function getMovableFeastDay(eventName) {
  if (!eventName) return null;

  const normalizedName = eventName.toLowerCase().trim();

  // Check against all movable feast patterns
  for (const [, feast] of Object.entries(MOVABLE_FEAST_PATTERNS)) {
    // Check if any keyword matches the event name
    const matches = feast.keywords.some((keyword) =>
      normalizedName.includes(keyword.toLowerCase())
    );

    if (matches) {
      return feast;
    }
  }

  return null;
}

/**
 * Get feast day information (checks both fixed and movable)
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @param {string} eventName - Event name from calendar (optional)
 * @returns {Object|null} - Feast day info with tone, or null
 */
export function getFeastDay(date, eventName = null) {
  // Parse date if string
  let dateObj = date;
  if (typeof date === "string") {
    const [year, month, day] = date.split("-").map(Number);
    dateObj = new Date(Date.UTC(year, month - 1, day));
  }

  // Priority 1: Check movable feast by event name (highest priority)
  if (eventName) {
    const movableFeast = getMovableFeastDay(eventName);
    if (movableFeast && movableFeast.tone !== null) {
      return movableFeast;
    }
  }

  // Priority 2: Check fixed feast by date
  const fixedFeast = getFixedFeastDay(dateObj);
  if (fixedFeast) {
    return fixedFeast;
  }

  return null;
}

/**
 * Check if a date or event is a feast day
 * @param {Date|string} date - Date object or YYYY-MM-DD string
 * @param {string} eventName - Event name from calendar (optional)
 * @returns {boolean}
 */
export function isFeastDay(date, eventName = null) {
  return getFeastDay(date, eventName) !== null;
}

/**
 * Get all feast days for display/reference
 * @returns {Object} - All feast day mappings
 */
export function getAllFeastDays() {
  return {
    fixed: FIXED_FEAST_DAYS,
    movable: MOVABLE_FEAST_PATTERNS,
  };
}

/**
 * Check if a feast day is a reset point
 * @param {Object} feastDay - Feast day object
 * @returns {boolean}
 */
export function isResetPoint(feastDay) {
  return feastDay && feastDay.isResetPoint === true;
}
